import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { remindersService } from '../../services/remindersService';
import ReminderCalendar from './ReminderCalendar';
import ReminderFormModal from './ReminderFormModal';
import './Reminders.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDateTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  });
};

const formatTimeOnly = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso), n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};

const isOverdue = (r) => {
  if (!r.ReminderTime || r.IsCompleted) return false;
  return (r.SnoozeUntil ? new Date(r.SnoozeUntil) : new Date(r.ReminderTime)) < new Date();
};

const PRESETS = [
  { label: '10 min',   key: '10m',      ms: 10  * 60000 },
  { label: '15 min',   key: '15m',      ms: 15  * 60000 },
  { label: '30 min',   key: '30m',      ms: 30  * 60000 },
  { label: '1 hour',   key: '1h',       ms: 60  * 60000 },
  { label: '2 hours',  key: '2h',       ms: 120 * 60000 },
  { label: '3 hours',  key: '3h',       ms: 180 * 60000 },
  { label: 'Tomorrow', key: 'tomorrow', ms: null },
  { label: 'Custom…',  key: 'custom',   ms: null },
];

const buildReminderTime = (preset, customDatetime) => {
  if (!preset) return null;
  if (preset === 'custom') return customDatetime ? new Date(customDatetime).toISOString() : null;
  if (preset === 'tomorrow') {
    const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0);
    return d.toISOString();
  }
  const found = PRESETS.find(p => p.key === preset);
  return found ? new Date(Date.now() + found.ms).toISOString() : null;
};

const toLocalInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso), pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EMPTY_FORM = {
  title: '', notes: '', preset: null, customDatetime: '',
  assignedTo: '',
  orderQuery: '', linkedOrderNo: '', linkedOrderDisplay: '',
  customerQuery: '', linkedCustomerID: null, linkedCustomerName: '',
  showMore: false,
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function RemindersPage({ user }) {
  const navigate = useNavigate();
  const [reminders, setReminders]   = useState([]);
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [editingId, setEditingId]       = useState(null);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [openWithDatetime, setOpenWithDatetime] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [quickTitle, setQuickTitle] = useState('');
  const [orderResults, setOrderResults]     = useState([]);
  const [customerResults, setCustomerResults] = useState([]);
  const quickInputRef = useRef(null);
  const orderTimerRef = useRef(null);
  const customerTimerRef = useRef(null);

  const username = user?.UserName;

  // ── Todo drag-to-reorder (state only — handlers defined after sortedTodos) ─
  const orderKey   = username ? `todo-order-${username}`   : null;
  const snoozedKey = username ? `todo-snoozed-${username}` : null;
  const [todoOrder, setTodoOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`todo-order-${username}`) || '[]'); } catch { return []; }
  });
  const [snoozedIds, setSnoozedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(`todo-snoozed-${username}`) || '[]')); } catch { return new Set(); }
  });
  const dragItemId  = useRef(null);
  const dragSource  = useRef(null); // 'todo' | 'snooze'
  const [dragOverId, setDragOverId]         = useState(null);
  const [dragOverSnooze, setDragOverSnooze] = useState(false);

  const saveSnoozed = (newSet) => {
    setSnoozedIds(newSet);
    if (snoozedKey) localStorage.setItem(snoozedKey, JSON.stringify([...newSet]));
  };

  const load = useCallback(async () => {
    if (!username) return;
    try {
      const [data, userList] = await Promise.all([
        remindersService.getAll(username),
        remindersService.getUsers(),
      ]);
      setReminders(data);
      setUsers(userList);
    } catch (err) {
      console.error('Failed to load reminders:', err);
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => { load(); }, [load]);

  // ── Derived lists ─────────────────────────────────────────────────────────
  const mine      = reminders.filter(r => r.UserName === username);
  const assigned  = reminders.filter(r => r.AssignedTo === username);

  const allActive     = mine.filter(r => !r.IsCompleted);
  const overdue       = allActive.filter(r => isOverdue(r));
  const dueToday      = allActive.filter(r => r.ReminderTime && isToday(r.ReminderTime) && !isOverdue(r));
  const upcoming      = allActive.filter(r => r.ReminderTime && !isToday(r.ReminderTime) && !isOverdue(r));
  const todos         = allActive.filter(r => !r.ReminderTime);
  const sortedTodos   = [...todos].sort((a, b) => {
    const ai = todoOrder.indexOf(a.ReminderID);
    const bi = todoOrder.indexOf(b.ReminderID);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  const activeTodos  = sortedTodos.filter(r => !snoozedIds.has(r.ReminderID));
  const snoozedTodos = sortedTodos.filter(r =>  snoozedIds.has(r.ReminderID));
  const completed     = mine.filter(r => r.IsCompleted);
  const completedToday = completed.filter(r => isToday(r.UpdatedAt));
  const todoTotal     = todos.length + completedToday.length;
  const todoPct       = todoTotal > 0 ? Math.round((completedToday.length / todoTotal) * 100) : 0;

  const assignedActive      = assigned.filter(r => !r.IsCompleted);
  const assignedCompleted   = assigned.filter(r => r.IsCompleted);
  const assignedTimedActive = assignedActive.filter(r => r.ReminderTime);
  const assignedTodosActive = assignedActive.filter(r => !r.ReminderTime);

  // ── Todo drag handlers (defined after activeTodos / snoozedTodos) ─────────
  const handleTodoDragStart   = (id, src = 'todo') => { dragItemId.current = id; dragSource.current = src; };
  const handleTodoDragOver    = (e, id) => { e.preventDefault(); setDragOverId(id); };
  const handleTodoDragEnd     = () => { dragItemId.current = null; dragSource.current = null; setDragOverId(null); setDragOverSnooze(false); };

  const handleTodoDrop = (e, targetId) => {
    e.preventDefault();
    const sourceId = dragItemId.current;
    if (!sourceId) { setDragOverId(null); return; }

    if (dragSource.current === 'snooze') {
      // Unsnooze and place before targetId in the active list
      const newSnoozed = new Set(snoozedIds);
      newSnoozed.delete(sourceId);
      saveSnoozed(newSnoozed);
      if (sourceId !== targetId) {
        const ids = activeTodos.map(r => r.ReminderID);
        const tgtIdx = ids.indexOf(targetId);
        const base   = todoOrder.filter(id => id !== sourceId);
        const insertAt = tgtIdx === -1 ? base.length : base.indexOf(targetId);
        base.splice(insertAt === -1 ? base.length : insertAt, 0, sourceId);
        setTodoOrder(base);
        if (orderKey) localStorage.setItem(orderKey, JSON.stringify(base));
      }
    } else {
      if (sourceId === targetId) { setDragOverId(null); return; }
      const ids    = activeTodos.map(r => r.ReminderID);
      const newIds = [...ids];
      newIds.splice(ids.indexOf(sourceId), 1);
      newIds.splice(ids.indexOf(targetId), 0, sourceId);
      setTodoOrder(newIds);
      if (orderKey) localStorage.setItem(orderKey, JSON.stringify(newIds));
    }
    setDragOverId(null);
  };

  // Drop a todo item onto the snooze column
  const handleSnoozeColumnDragOver  = (e) => { e.preventDefault(); setDragOverSnooze(true); };
  const handleSnoozeColumnDragLeave = ()  => setDragOverSnooze(false);
  const handleSnoozeColumnDrop      = (e) => {
    e.preventDefault();
    const sourceId = dragItemId.current;
    if (sourceId && dragSource.current === 'todo') {
      const newSnoozed = new Set(snoozedIds);
      newSnoozed.add(sourceId);
      saveSnoozed(newSnoozed);
    }
    setDragOverSnooze(false);
  };

  // Drop a snoozed item back onto the todo column
  const handleTodoColumnDragOver = (e) => { if (dragSource.current === 'snooze') e.preventDefault(); };
  const handleTodoColumnDrop     = (e) => {
    e.preventDefault();
    const sourceId = dragItemId.current;
    if (sourceId && dragSource.current === 'snooze') {
      const newSnoozed = new Set(snoozedIds);
      newSnoozed.delete(sourceId);
      saveSnoozed(newSnoozed);
    }
  };

  const handleUnsnooze = (id) => {
    const newSnoozed = new Set(snoozedIds);
    newSnoozed.delete(id);
    saveSnoozed(newSnoozed);
  };

  // ── User map for full name lookups ────────────────────────────────────────
  const userMap = Object.fromEntries(
    users.map(u => [u.UserName, u.UserFullName || `${u.UserFirstName} ${u.UserLastName}`.trim()])
  );

  // ── Form helpers ──────────────────────────────────────────────────────────
  const openCreateForm = () => { setEditingId(null); setForm(EMPTY_FORM); setOrderResults([]); setCustomerResults([]); setShowForm(true); };

  const openCreateFormForDay = (dateKey) => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpenWithDatetime(`${dateKey}T09:00`);
    setOrderResults([]); setCustomerResults([]);
    setShowForm(true);
  };

  const openEditForm = (r) => {
    setEditingId(r.ReminderID);
    setForm({
      title: r.Title, notes: r.Notes || '',
      preset: r.ReminderTime ? 'custom' : null,
      customDatetime: toLocalInputValue(r.ReminderTime),
      assignedTo: r.AssignedTo || '',
      orderQuery: r.LinkedOrderNo || '', linkedOrderNo: r.LinkedOrderNo || '',
      linkedOrderDisplay: r.LinkedOrderNo ? `Order #${r.LinkedOrderNo}` : '',
      customerQuery: r.LinkedCustomerName || '', linkedCustomerID: r.LinkedCustomerID || null,
      linkedCustomerName: r.LinkedCustomerName || '',
      showMore: !!(r.AssignedTo || r.LinkedOrderNo || r.LinkedCustomerName),
    });
    setOrderResults([]); setCustomerResults([]);
    setShowForm(true);
  };

  const cancelForm = () => { setShowForm(false); setEditingId(null); setOpenWithDatetime(null); setOrderResults([]); setCustomerResults([]); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const reminderTime = buildReminderTime(form.preset, form.customDatetime);
      const extras = {
        assignedTo:         form.assignedTo || null,
        assignedBy:         form.assignedTo ? username : null,
        linkedOrderNo:      form.linkedOrderNo || null,
        linkedCustomerID:   form.linkedCustomerID || null,
        linkedCustomerName: form.linkedCustomerName || null,
      };
      if (editingId) {
        const updated = await remindersService.update(editingId, username, {
          title: form.title, notes: form.notes || null, reminderTime, ...extras
        });
        setReminders(prev => prev.map(r => r.ReminderID === editingId ? updated : r));
      } else {
        const created = await remindersService.create(username, form.title, form.notes || null, reminderTime, extras);
        setReminders(prev => [created, ...prev]);
      }
      cancelForm();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  // ── Order search (debounced) ──────────────────────────────────────────────
  const handleOrderQuery = (val) => {
    setForm(f => ({ ...f, orderQuery: val, linkedOrderNo: '', linkedOrderDisplay: '' }));
    clearTimeout(orderTimerRef.current);
    if (val.length < 2) { setOrderResults([]); return; }
    orderTimerRef.current = setTimeout(async () => {
      try { setOrderResults(await remindersService.searchOrders(val)); } catch (_) {}
    }, 300);
  };

  const selectOrder = (o) => {
    setForm(f => ({ ...f, linkedOrderNo: o.OrderNo, linkedOrderDisplay: `#${o.OrderNo} — ${o.CustomerName || ''}`, orderQuery: `#${o.OrderNo} — ${o.CustomerName || ''}` }));
    setOrderResults([]);
  };

  // ── Customer search (debounced) ───────────────────────────────────────────
  const handleCustomerQuery = (val) => {
    setForm(f => ({ ...f, customerQuery: val, linkedCustomerID: null, linkedCustomerName: '' }));
    clearTimeout(customerTimerRef.current);
    if (val.length < 2) { setCustomerResults([]); return; }
    customerTimerRef.current = setTimeout(async () => {
      try { setCustomerResults(await remindersService.searchCustomers(val)); } catch (_) {}
    }, 300);
  };

  const selectCustomer = (c) => {
    const name = c.CustomerFullName || `${c.CustomerFirstName} ${c.CustomerLastName}`;
    setForm(f => ({ ...f, linkedCustomerID: c.ID, linkedCustomerName: name, customerQuery: name }));
    setCustomerResults([]);
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const toggleComplete = async (r) => {
    try {
      const updated = await remindersService.update(r.ReminderID, username, { isCompleted: !r.IsCompleted });
      setReminders(prev => prev.map(x => x.ReminderID === r.ReminderID ? updated : x));
    } catch (err) { console.error('Toggle failed:', err); }
  };

  const handleDelete = async (id) => {
    // if (!window.confirm('Delete this reminder?')) return;
    try {
      await remindersService.remove(id, username);
      setReminders(prev => prev.filter(r => r.ReminderID !== id));
    } catch (err) { console.error('Delete failed:', err); }
  };

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    if (!quickTitle.trim()) return;
    try {
      const created = await remindersService.create(username, quickTitle.trim());
      setReminders(prev => [...prev, created]);
      setQuickTitle('');
      quickInputRef.current?.focus();
    } catch (err) { console.error('Quick-add failed:', err); }
  };

  if (loading) return <div className="reminders-loading">Loading reminders...</div>;

  const otherUsers = users.filter(u => u.UserName !== username);

  return (
    <div className="reminders-page">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="reminders-header">
        <h1><i className="fas fa-bell"></i> My Reminders</h1>
        <button className="btn-add-reminder" onClick={openCreateForm}>
          <i className="fas fa-plus"></i> New Alert
        </button>
      </div>

      {/* ── Stats strip ───────────────────────────────────────────────────── */}
      <div className="stats-strip">
        <div className={`stat-card ${overdue.length > 0 ? 'stat-danger' : ''}`}>
          <span className="stat-num">{overdue.length}</span>
          <span className="stat-label">Overdue</span>
        </div>
        <div className={`stat-card ${dueToday.length > 0 ? 'stat-warning' : ''}`}>
          <span className="stat-num">{dueToday.length}</span>
          <span className="stat-label">Due Today</span>
        </div>
        <div className="stat-card">
          <span className="stat-num">{upcoming.length}</span>
          <span className="stat-label">Upcoming</span>
        </div>
        <div className="stat-card">
          <span className="stat-num">{todos.length}</span>
          <span className="stat-label">To-Dos</span>
        </div>
        <div className="stat-card stat-done">
          <span className="stat-num">{completed.length}</span>
          <span className="stat-label">Done</span>
        </div>
        {(assignedActive.length > 0 || assignedCompleted.length > 0) && (
          <div className="stat-card stat-assigned">
            <span className="stat-num">{assignedActive.length}</span>
            <span className="stat-label">Assigned to Me</span>
            {assignedCompleted.length > 0 && <span className="stat-sub">{assignedCompleted.length} done</span>}
          </div>
        )}
      </div>

      {/* ── Create / Edit form ────────────────────────────────────────────── */}
      {showForm && (
        <ReminderFormModal
          username={username}
          editingReminder={editingId ? reminders.find(r => r.ReminderID === editingId) : null}
          defaultDatetime={openWithDatetime}
          onClose={cancelForm}
          onSaved={(result) => {
            if (editingId) {
              setReminders(prev => prev.map(r => r.ReminderID === editingId ? result : r));
            } else {
              setReminders(prev => [result, ...prev]);
            }
          }}
        />
      )}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* 3-COLUMN LAYOUT: Alerts | To-Do + Completed | Calendar + Parked    */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div className="reminders-columns">

        {/* ── COL 1: Alerts ─────────────────────────────────────────────── */}
        <div className="reminders-main-col reminders-main-col-alerts">
          <div className="reminders-col">
            <h2 className="col-heading"><i className="fas fa-bell"></i> Alerts</h2>

            {overdue.length > 0 && (
              <section className="reminders-section">
                <h3 className="section-overdue"><i className="fas fa-exclamation-circle"></i> Overdue</h3>
                <ul className="reminders-list">
                  {overdue.map(r => <ReminderItem key={r.ReminderID} reminder={r} overdue userMap={userMap} username={username} onToggle={toggleComplete} onEdit={openEditForm} onDelete={handleDelete} navigate={navigate} />)}
                </ul>
              </section>
            )}

            <section className="reminders-section">
              <h3 className="section-today"><i className="fas fa-sun"></i> Due Today</h3>
              {dueToday.length === 0
                ? <p className="empty-state">Nothing due today.</p>
                : <ul className="reminders-list">{dueToday.map(r => <ReminderItem key={r.ReminderID} reminder={r} showTimeOnly userMap={userMap} username={username} onToggle={toggleComplete} onEdit={openEditForm} onDelete={handleDelete} navigate={navigate} />)}</ul>}
            </section>

            {upcoming.length > 0 && (
              <section className="reminders-section">
                <h3><i className="fas fa-calendar-alt"></i> Upcoming</h3>
                <ul className="reminders-list">
                  {upcoming.map(r => <ReminderItem key={r.ReminderID} reminder={r} userMap={userMap} username={username} onToggle={toggleComplete} onEdit={openEditForm} onDelete={handleDelete} navigate={navigate} />)}
                </ul>
              </section>
            )}

            {(assignedTimedActive.length > 0 || assignedCompleted.length > 0) && (
              <section className="reminders-section">
                <h3 className="section-assigned"><i className="fas fa-user-check"></i> Assigned to Me</h3>
                {assignedTimedActive.length > 0 && (
                  <ul className="reminders-list">
                    {assignedTimedActive.map(r => <ReminderItem key={r.ReminderID} reminder={r} overdue={isOverdue(r)} userMap={userMap} username={username} onToggle={toggleComplete} onEdit={openEditForm} onDelete={handleDelete} navigate={navigate} />)}
                  </ul>
                )}
                {assignedCompleted.length > 0 && (
                  <>
                    <p className="assigned-completed-label"><i className="fas fa-check-circle"></i> Completed</p>
                    <ul className="reminders-list">
                      {assignedCompleted.map(r => <ReminderItem key={r.ReminderID} reminder={r} userMap={userMap} username={username} onToggle={toggleComplete} onEdit={openEditForm} onDelete={handleDelete} navigate={navigate} />)}
                    </ul>
                  </>
                )}
              </section>
            )}

            {overdue.length === 0 && dueToday.length === 0 && upcoming.length === 0 && assignedTimedActive.length === 0 && (
              <p className="empty-state col-empty">
                <i className="fas fa-check-circle"></i><br />No alerts scheduled.<br />
                <button className="empty-cta" onClick={openCreateForm}>Add one</button>
              </p>
            )}
          </div>
        </div>

        {/* ── COL 2: To-Do + Completed (stacked) ────────────────────────── */}
        <div className="reminders-main-col reminders-main-col-todos">

          <div
            className="reminders-col reminders-col-todos"
            onDragOver={handleTodoColumnDragOver}
            onDrop={handleTodoColumnDrop}
          >
            <div className="col-heading-row">
              <h2 className="col-heading"><i className="fas fa-list-check"></i> To-Do List</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {snoozedTodos.length > 0 && (
                  <span className="todo-parked-hint">{snoozedTodos.length} parked</span>
                )}
                {todoTotal > 0 && <span className="todo-progress-label">{completedToday.length}/{todoTotal} done today</span>}
              </div>
            </div>

            {todoTotal > 0 && (
              <div className="progress-bar-track">
                <div className="progress-bar-fill" style={{ width: `${todoPct}%` }} />
              </div>
            )}

            {activeTodos.length === 0 && <p className="empty-state">All clear! Add something below.</p>}
            {activeTodos.length > 0 && (
              <ul className="reminders-list">
                {activeTodos.map(r => (
                  <ReminderItem
                    key={r.ReminderID}
                    reminder={r}
                    userMap={userMap}
                    username={username}
                    onToggle={toggleComplete}
                    onEdit={openEditForm}
                    onDelete={handleDelete}
                    navigate={navigate}
                    dragHandlers={{
                      draggable: true,
                      onDragStart: () => handleTodoDragStart(r.ReminderID, 'todo'),
                      onDragOver:  (e) => handleTodoDragOver(e, r.ReminderID),
                      onDrop:      (e) => handleTodoDrop(e, r.ReminderID),
                      onDragEnd:   handleTodoDragEnd,
                    }}
                    isDragOver={dragOverId === r.ReminderID}
                  />
                ))}
              </ul>
            )}

            {assignedTodosActive.length > 0 && (
              <section className="reminders-section assigned-todos-section">
                <h3 className="section-assigned"><i className="fas fa-user-check"></i> Assigned to Me</h3>
                <ul className="reminders-list">
                  {assignedTodosActive.map(r => (
                    <ReminderItem key={r.ReminderID} reminder={r} userMap={userMap} username={username} onToggle={toggleComplete} onEdit={openEditForm} onDelete={handleDelete} navigate={navigate} />
                  ))}
                </ul>
              </section>
            )}

            <form className="quick-add-form" onSubmit={handleQuickAdd}>
              <i className="fas fa-plus quick-add-icon"></i>
              <input ref={quickInputRef} type="text" className="quick-add-input"
                placeholder="Add a task and press Enter…" value={quickTitle}
                onChange={e => setQuickTitle(e.target.value)} maxLength={200} />
            </form>
          </div>

          {completed.length > 0 && (
            <div className="reminders-col reminders-col-completed">
              <section className="reminders-section completed-section">
                <h3><i className="fas fa-check-circle"></i> Completed ({completed.length})</h3>
                <ul className="reminders-list">
                  {completed.map(r => <ReminderItem key={r.ReminderID} reminder={r} userMap={userMap} username={username} onToggle={toggleComplete} onEdit={openEditForm} onDelete={handleDelete} navigate={navigate} />)}
                </ul>
              </section>
            </div>
          )}

        </div>

        {/* ── COL 3: Calendar + Parked (stacked) ────────────────────────── */}
        <div className="reminders-main-col reminders-main-col-cal">

          <div className="reminders-col">
            <h2 className="col-heading"><i className="fas fa-calendar-alt"></i> Calendar</h2>
            <ReminderCalendar reminders={reminders} onAddForDay={openCreateFormForDay} />
          </div>

          <div
            className={`reminders-col reminders-col-snooze${dragOverSnooze ? ' snooze-drag-over' : ''}`}
            onDragOver={handleSnoozeColumnDragOver}
            onDragLeave={handleSnoozeColumnDragLeave}
            onDrop={handleSnoozeColumnDrop}
          >
            <h2 className="col-heading col-heading-snooze">
              <i className="fas fa-archive"></i> Parked
              {snoozedTodos.length > 0 && <span className="snooze-count">{snoozedTodos.length}</span>}
            </h2>
            {snoozedTodos.length === 0 ? (
              <div className="snooze-empty">
                <i className="fas fa-arrow-left"></i>
                Drag tasks here to park them for later
              </div>
            ) : (
              <ul className="reminders-list snooze-list">
                {snoozedTodos.map(r => (
                  <ReminderItem
                    key={r.ReminderID}
                    reminder={r}
                    userMap={userMap}
                    username={username}
                    onToggle={toggleComplete}
                    onEdit={openEditForm}
                    onDelete={handleDelete}
                    navigate={navigate}
                    dragHandlers={{
                      draggable: true,
                      onDragStart: () => handleTodoDragStart(r.ReminderID, 'snooze'),
                      onDragEnd:   handleTodoDragEnd,
                    }}
                    extraAction={
                      <button className="action-btn unsnooze-btn" onClick={() => handleUnsnooze(r.ReminderID)} title="Move back to To-Do">
                        <i className="fas fa-undo"></i>
                      </button>
                    }
                  />
                ))}
              </ul>
            )}
          </div>

        </div>

      </div>
    </div>
  );
}

// ── Reminder list item ────────────────────────────────────────────────────────

function ReminderItem({ reminder: r, overdue, showTimeOnly, userMap = {}, username, onToggle, onEdit, onDelete, navigate, dragHandlers, isDragOver, extraAction }) {
  const snoozed  = r.SnoozeUntil && new Date(r.SnoozeUntil) > new Date();
  const isAssignedToMe = r.AssignedTo === username;
  const timeLabel = snoozed
    ? `Snoozed until ${formatDateTime(r.SnoozeUntil)}`
    : showTimeOnly ? formatTimeOnly(r.ReminderTime) : formatDateTime(r.ReminderTime);

  return (
    <li
      className={`reminder-item ${r.IsCompleted ? 'completed' : ''} ${overdue ? 'overdue' : ''} ${isDragOver ? 'drag-over' : ''}`}
      {...(dragHandlers || {})}
    >
      {dragHandlers && <span className="drag-handle" title="Drag to reorder"><i className="fas fa-grip-vertical"></i></span>}
      <button className="check-btn" onClick={() => onToggle(r)} title={r.IsCompleted ? 'Mark incomplete' : 'Mark complete'}>
        <i className={`fas ${r.IsCompleted ? 'fa-check-circle' : 'fa-circle'}`}></i>
      </button>
      <div className="reminder-body">
        <span className="reminder-title">{r.Title}</span>
        {r.Notes && <span className="reminder-notes">{r.Notes}</span>}

        {/* Time */}
        {r.ReminderTime && (
          <span className={`reminder-time ${overdue ? 'overdue-text' : ''}`}>
            <i className="fas fa-clock"></i> {timeLabel}
            {overdue && <span className="overdue-badge">Overdue</span>}
            {snoozed && <span className="snoozed-badge"><i className="fas fa-moon"></i> Snoozed</span>}
          </span>
        )}

        {/* Assignment badges */}
        {r.AssignedTo && !isAssignedToMe && (
          <span className="assign-badge assign-out"><i className="fas fa-arrow-right"></i> {userMap[r.AssignedTo] || r.AssignedTo}</span>
        )}
        {isAssignedToMe && (r.AssignedBy || r.UserName) && (
          <span className="assign-badge assign-in"><i className="fas fa-arrow-left"></i> from {userMap[r.AssignedBy || r.UserName] || r.AssignedBy || r.UserName}</span>
        )}

        {/* Linked badges */}
        <div className="link-badges">
          {r.LinkedOrderNo && (
            <button className="link-badge" onClick={() => navigate(`/work-order-profile/${r.LinkedOrderNo}`)}>
              <i className="fas fa-file-alt"></i> #{r.LinkedOrderNo}
            </button>
          )}
          {r.LinkedCustomerName && (
            <button className="link-badge" onClick={() => r.LinkedCustomerID && navigate(`/customers/${r.LinkedCustomerID}`)}>
              <i className="fas fa-user"></i> {r.LinkedCustomerName}
            </button>
          )}
        </div>
      </div>
      <div className="reminder-actions">
        {extraAction}
        <button className="action-btn edit-btn" onClick={() => onEdit(r)} title="Edit"><i className="fas fa-pencil-alt"></i></button>
        <button className="action-btn delete-btn" onClick={() => onDelete(r.ReminderID)} title="Delete"><i className="fas fa-trash"></i></button>
      </div>
    </li>
  );
}

